import React, { useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { COMPETITORS, simulateCompetitor } from '../../data/competitors';
import { formatMoney } from '../../utils/format';
import { Trophy, TrendingUp, TrendingDown, Minus, Crown, Truck, Star, Medal, Zap, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../i18n';

const RANK_STYLES = [
  { bg: 'bg-warn/10', border: 'border-warn/60', icon: Crown, iconColor: 'text-warn', label: '1er' },
  { bg: 'bg-ink-700/30', border: 'border-ink-500', icon: Medal, iconColor: 'text-slate-500', label: '2e' },
  { bg: 'bg-orange-50', border: 'border-orange-300', icon: Medal, iconColor: 'text-orange-400', label: '3e' },
];

const IMPACT_STYLE = {
  threat: { bg: 'bg-danger/10', border: 'border-danger/40', icon: AlertTriangle, color: 'text-danger' },
  opportunity: { bg: 'bg-money/10', border: 'border-money/40', icon: Zap, color: 'text-money' },
  neutral: { bg: 'bg-accent/10', border: 'border-accent/40', icon: Zap, color: 'text-accent' },
};

export const Leaderboard = () => {
  const { t } = useTranslation();
  const { company, monthlyRevenues, reputation, vehicles, month, year, totalDays } = useGameStore();

  const monthNumber = ((year - 1) * 12) + month;

  const { rankings, newsItem } = useMemo(() => {
    const playerRev = Math.round(monthlyRevenues);
    const competitors = COMPETITORS.map(c => {
      const stats = simulateCompetitor(c, totalDays, reputation, playerRev);
      return {
        id: c.id, name: c.name, city: c.specialties[0] || '—',
        specialty: c.style, color: c.color, initials: c.avatar,
        logo: c.logo || null,
        monthlyRevenue: stats.ca, reputation: stats.reputation,
        currentFleet: stats.fleet, isPlayer: false, strategy: c.personality,
      };
    });

    const player = {
      id: 'player',
      name: company?.name || 'Ma Société',
      city: company?.city || '—',
      specialty: company?.specialty || '—',
      color: '#1F3864',
      initials: (company?.name || 'MS').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
      monthlyRevenue: playerRev,
      reputation: Math.round(reputation),
      currentFleet: vehicles.length,
      isPlayer: true,
      strategy: 'Vous',
    };

    const all = [...competitors, player];
    all.sort((a, b) => b.monthlyRevenue - a.monthlyRevenue);
    const ranked = all.map((entry, idx) => ({ ...entry, rank: idx + 1 }));

    // Generate a simple news item based on ranking
    const topCompetitor = ranked.find(r => !r.isPlayer);
    const news = topCompetitor ? {
      title: `${topCompetitor.name} — Actualité`,
      message: COMPETITORS.find(c => c.id === topCompetitor.id)?.reactions.neutral || 'Activité en cours.',
      impact: ranked[0]?.isPlayer ? 'opportunity' : 'neutral',
    } : null;

    return { rankings: ranked, newsItem: news };
  }, [totalDays, monthlyRevenues, reputation, vehicles.length, company]);

  const playerRank = rankings.find(r => r.isPlayer)?.rank || rankings.length;
  const playerPrev = playerRank; // simplified — no previous month tracking

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid="leaderboard">
      {/* Header */}
      <div className="bg-accent px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-warn" />
          <h3 className="text-sm font-bold text-foreground">{t('market_ranking_title')}</h3>
        </div>
        <span className="text-xs text-blue-200">Mois {month} — An {year}</span>
      </div>

      {/* Player rank summary */}
      <div className="px-4 py-3 bg-accent/10 border-b border-blue-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
            playerRank === 1 ? 'bg-warn/100 text-white' :
            playerRank <= 3 ? 'bg-accent text-white' :
            'bg-gray-300 text-slate-300'
          }`}>
            {playerRank}
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">{t('your_position')}</p>
            <p className="text-xs text-slate-500">
              {playerRank === 1 ? 'Leader du marché !' :
               playerRank <= 3 ? 'Podium — continuez !' :
               'En progression...'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-foreground">{formatMoney(monthlyRevenues)}</p>
          <p className="text-xs text-slate-500">rev./mois</p>
        </div>
      </div>

      {/* Rankings table */}
      <div className="divide-y divide-gray-100">
        {rankings.map((entry, idx) => {
          const isPlayer = entry.isPlayer;
          const rankStyle = RANK_STYLES[idx] || null;
          const RankIcon = rankStyle?.icon || null;

          return (
            <div
              key={entry.id}
              className={`px-4 py-2.5 flex items-center gap-3 transition-colors ${
                isPlayer ? 'bg-accent/10/70 border-l-[3px] border-l-[#1F3864]' : 'hover:bg-ink-700/40'
              }`}
              data-testid={`leaderboard-rank-${entry.rank}`}
            >
              {/* Rank */}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                idx === 0 ? 'bg-warn/15 text-warn' :
                idx === 1 ? 'bg-ink-700/40 text-slate-400' :
                idx === 2 ? 'bg-orange-100 text-orange-600' :
                'bg-ink-700/30 text-slate-500'
              }`}>
                {RankIcon ? <RankIcon className={`w-3.5 h-3.5 ${rankStyle.iconColor}`} /> : entry.rank}
              </div>

              {/* Company avatar */}
              {entry.logo ? (
                <img
                  src={entry.logo}
                  alt={entry.name}
                  className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border"
                  style={{ borderColor: entry.color }}
                />
              ) : (
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: entry.color }}
                >
                  {entry.initials}
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate ${isPlayer ? 'text-foreground' : 'text-slate-200'}`}>
                  {entry.name}
                  {isPlayer && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-accent text-white">VOUS</span>}
                </p>
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  <span>{entry.city}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                  <span>{entry.specialty}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                  <span className="flex items-center gap-0.5"><Truck className="w-2.5 h-2.5" />{entry.currentFleet}</span>
                </div>
              </div>

              {/* Revenue + Reputation */}
              <div className="text-right flex-shrink-0">
                <p className={`text-sm font-bold ${isPlayer ? 'text-foreground' : 'text-slate-300'}`}>
                  {formatMoney(entry.monthlyRevenue)}
                </p>
                <div className="flex items-center gap-1 justify-end">
                  <Star className="w-2.5 h-2.5 text-warn" />
                  <span className="text-[10px] text-slate-500">{entry.reputation}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Competitor News */}
      {newsItem && (
        <div className={`mx-3 my-2 p-3 rounded-lg border ${IMPACT_STYLE[newsItem.impact]?.bg} ${IMPACT_STYLE[newsItem.impact]?.border}`} data-testid="competitor-news">
          {(() => { const ImpIcon = IMPACT_STYLE[newsItem.impact]?.icon || Zap; return (
            <div className="flex items-start gap-2">
              <ImpIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${IMPACT_STYLE[newsItem.impact]?.color}`} />
              <div>
                <p className={`text-xs font-semibold ${IMPACT_STYLE[newsItem.impact]?.color}`}>{newsItem.title}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{newsItem.message}</p>
              </div>
            </div>
          ); })()}
        </div>
      )}

      {/* Footer tip */}
      <div className="px-4 py-2 bg-ink-700/30 border-t border-ink-600">
        <p className="text-[10px] text-slate-500 text-center">
          Le classement se met à jour chaque mois en fonction du CA et de la réputation
        </p>
      </div>
    </div>
  );
};

export default Leaderboard;
